/* eslint-disable react-hooks/exhaustive-deps */
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Card, Form } from "react-bootstrap";

import { AppRulesContext } from "../../../App";
import MSGFilingApi from "../../../api/MSGFilingApi";
import { EToastvariant, IActiveMDTTabs, IEfilingMessage } from "../../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import { showToastMsg } from "../../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../../services";
import FASvgIcon from "../../FASvgIcon";
import { ChatContainerBody } from "../../itr-filing/assisted";
import { LoadingIndicator } from "../../placeholders";

let INTERVAL: any;
type Props = {
  activeMDT: IActiveMDTTabs;
};

const MessagesTab: React.FC<Props> = (props: Props) => {
  const { activeMDT } = props;

  const dispatch = useAppDispatch();
  const { rulesSvc } = useContext(AppRulesContext);
  const { currentFilingUUID } = useAppSelector(ITRFilingSelector);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [chatMessage, setChatMessage] = useState<string>("");
  const [isSyncMsg, setIsSyncMsg] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [EFilingMessages, setEFillingMessages] = useState<IEfilingMessage[]>([]);
  const msgFilingApi = MSGFilingApi.getInstance();

  // only call when message tab active and show canvas open
  useEffect(() => {
    if (isSyncMsg && activeMDT === "messages") {
      INTERVAL = setInterval(() => {
        getAllEfilingMessages(false);
      }, 5000);
    }
    return () => clearInterval(INTERVAL);
  }, [isSyncMsg, activeMDT]);

  // initially render to get efiling messages
  useEffect(() => {
    if (activeMDT === "messages") {
      getAllEfilingMessages(true);
    }
  }, [activeMDT]);

  const scrollToBottom = async () => {
    setTimeout(() => {
      if (containerRef?.current) {
        containerRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [EFilingMessages.length, activeMDT]);

  // get efiling msg
  const getAllEfilingMessages = async (showLoader = true) => {
    try {
      if (currentFilingUUID) {
        showLoader && setIsLoading(true);
        const { records } = await msgFilingApi.getFilingMsgs({
          offset: 0,
          count: -1,
          efilingUuid: currentFilingUUID,
          orderBy: "ASC"
        });
        setEFillingMessages(() => records);
        setIsSyncMsg(() => true);
      }
    } catch (error) {
      UtilService.getErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  // post efiling msg
  const postMessage = (messageObj: IEfilingMessage) => {
    const msgArray = EFilingMessages.slice();
    msgArray.push(messageObj);
    setEFillingMessages(() => msgArray);
  };

  // send efiling msg
  const handleSendMsg = async () => {
    try {
      if (!currentFilingUUID) {
        return;
      }

      // create efiling msg
      const _sendData = {
        efilingUuid: currentFilingUUID,
        type: "text",
        message: {
          text: chatMessage
        }
      };

      const resp = await msgFilingApi.createFilingMsg(_sendData);
      setChatMessage("");
      postMessage(resp?.data);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: `Something went wrong while sent the message`,
          variant: EToastvariant.Danger
        })
      );
    } finally {
    }
  };

  return (
    <Card
      className={classNames(
        "chat-card shadow-none m-0",
        !rulesSvc.canAccess(rulesSvc.createAndUpdateEFiling) && "only-chats"
      )}
      bg="transparent"
    >
      <Card.Body>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <>
            <ChatContainerBody messages={EFilingMessages} />
          </>
        )}
        <div ref={containerRef}></div>
      </Card.Body>
      {rulesSvc.canAccess(rulesSvc.createAndUpdateEFiling) && (
        <Card.Footer>
          <div className="chat-window-footer">
            <Form.Group className="form-group mb-0">
              <Form.Label className="h5 fw-bold">Type message:</Form.Label>
              <Form.Control
                as="textarea"
                autoComplete="false"
                name="chatMessage"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
              />
            </Form.Group>
            <Button
              variant="primary"
              size="sm"
              className="btn-send"
              onClick={() => handleSendMsg()}
              disabled={!!!chatMessage}
            >
              Send
              <FASvgIcon icon={faPaperPlane} />
            </Button>
          </div>
        </Card.Footer>
      )}
    </Card>
  );
};

export default MessagesTab;
